import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, ArrowRight, Loader2, Wifi, MapPin } from "lucide-react";
import PhotoDrop from "@/components/PhotoDrop";
import AddressSearch from "@/components/AddressSearch";

export default function CollabCreate() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const editId = sp.get("edit");
  const [meta, setMeta] = useState({ categories: [], work_types: [] });
  const [f, setF] = useState({
    title: "", description: "", category: "Tech",
    work_type: "remote", location: "", lat: null, lng: null,
    image: "", contact_note: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/collabs/categories").then(r => setMeta(r.data || {})).catch(()=>{});
  }, []);

  useEffect(() => {
    if (!editId) return;
    api.get(`/collabs/${editId}`).then(r => {
      const c = r.data || {};
      setF({
        title: c.title || "",
        description: c.description || "",
        category: c.category || "Tech",
        work_type: c.work_type || "remote",
        location: c.location || "",
        lat: c.lat ?? null, lng: c.lng ?? null,
        image: c.image || "",
        contact_note: c.contact_note || "",
      });
    }).catch(e => toast.error(formatErr(e)));
  }, [editId]);

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error("Give it a title"); return; }
    if (!f.description.trim()) { toast.error("Add a short description"); return; }
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/collabs/${editId}`, f);
        toast.success("Collab updated");
        nav(`/collabs/${editId}`);
      } else {
        const { data } = await api.post("/collabs", f);
        toast.success("Collab posted!");
        nav(`/collabs/${data.id}`);
      }
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={save} className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="collab-create-form">
      <div>
        <p className="overline">{editId ? "Edit collab" : "New collaboration"}</p>
        <h1 className="h-display text-4xl mt-1">Who do you want to team up with?</h1>
        <p className="text-[#4A4A4A] mt-1">Post it clearly — the right Hooman is probably one scroll away.</p>
      </div>

      <div className="card p-6 space-y-4">
        <div>
          <label className="text-sm font-medium">Title <span className="text-[#B84B4B]">*</span></label>
          <input className="input mt-1.5" value={f.title} onChange={(e)=>setF({...f, title:e.target.value})}
                 placeholder="e.g. Looking for a Rust dev to prototype a photo app" data-testid="collab-title-input" maxLength={160}/>
          <p className="text-[11px] text-[#8A8A8A] mt-1">This is what people scan on the feed — keep it punchy.</p>
        </div>

        <div>
          <label className="text-sm font-medium">Description <span className="text-[#B84B4B]">*</span></label>
          <textarea rows={5} className="input mt-1.5 resize-none" value={f.description} onChange={(e)=>setF({...f, description:e.target.value})}
                    placeholder="What are you building? Who are you looking for? What's the deal?"
                    data-testid="collab-desc-input"/>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Category</label>
            <select className="input mt-1.5" value={f.category} onChange={(e)=>setF({...f, category:e.target.value})} data-testid="collab-category-input">
              {(meta.categories?.length ? meta.categories : ["Tech","Art","Events"]).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Where</label>
            <div className="mt-1.5 flex gap-1">
              {[["remote", "Remote", Wifi], ["in_person", "In-person", MapPin], ["hybrid", "Hybrid", null]].map(([v,l,I]) => (
                <button type="button" key={v}
                  onClick={()=>setF({...f, work_type:v})}
                  data-testid={`collab-wt-${v}`}
                  className={`flex-1 text-xs px-3 py-2 rounded-full inline-flex items-center justify-center gap-1 ${f.work_type===v ? "bg-[#0A0A0A] text-white" : "border border-[#E5E5E3] text-[#4A4A4A] hover:bg-[#F2F2F0]"}`}>
                  {I && <I size={11}/>} {l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {(f.work_type === "in_person" || f.work_type === "hybrid") && (
          <div>
            <label className="text-sm font-medium">Location</label>
            <p className="text-[11px] text-[#8A8A8A] mt-0.5 mb-1.5">Search a real address — a live map pin will appear on the collab page.</p>
            <AddressSearch
              value={f.location}
              placeholder="Search a city or address…"
              testid="collab-location"
              onChange={(a)=>setF({...f, location: a.address || "", lat: a.lat ?? null, lng: a.lng ?? null})}
            />
          </div>
        )}

        <div>
          <label className="text-sm font-medium">Cover image (optional)</label>
          <div className="mt-1.5"><PhotoDrop value={f.image} onChange={(v)=>setF({...f, image:v})} aspect="cover" label="Drop a cover" testid="collab-image"/></div>
        </div>
      </div>

      <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="collab-submit">
        {busy ? <Loader2 size={14} className="animate-spin"/> : <><Handshake size={14}/> {editId ? "Save changes" : "Post collab"} <ArrowRight size={14}/></>}
      </button>
    </form>
  );
}
